import type { WorkspaceTask } from "../components/workspace/types";

export const USE_LOCAL_PREVIEW_SURFACE_DATA = import.meta.env.MODE !== "production" && !import.meta.env.VITE_FIREBASE_API_KEY;
export const LOCAL_PREVIEW_AGENT_RUNS_KEY = "iamv:local-preview:agent-runs";
export const LOCAL_PREVIEW_AGENT_RUNS_EVENT = "iamv:local-preview-agent-runs-changed";

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function coerceTaskRecord(value: unknown): WorkspaceTask | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Partial<WorkspaceTask>;
  if (typeof record.id !== "string" || typeof record.title !== "string" || typeof record.prompt !== "string") {
    return null;
  }

  return {
    id: record.id,
    title: record.title,
    prompt: record.prompt,
    status: record.status ?? "QUEUED",
    date: typeof record.date === "string" ? record.date : new Date().toISOString(),
    matterId: typeof record.matterId === "string" ? record.matterId : "",
    matterName: typeof record.matterName === "string" ? record.matterName : "Unlinked matter",
    executor: typeof record.executor === "string" ? record.executor : "Agent",
    progress: typeof record.progress === "number" ? record.progress : 0,
    steps: Array.isArray(record.steps) ? record.steps : [],
    hoursSaved: typeof record.hoursSaved === "number" ? record.hoursSaved : 0,
    recoveredAED: typeof record.recoveredAED === "number" ? record.recoveredAED : 0,
    isReported: record.isReported === true,
  };
}

export function readLocalPreviewAgentRuns(): WorkspaceTask[] {
  if (!isBrowser()) return [];

  try {
    const raw = window.localStorage.getItem(LOCAL_PREVIEW_AGENT_RUNS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.map(coerceTaskRecord).filter((record): record is WorkspaceTask => Boolean(record));
  } catch {
    return [];
  }
}

export function persistLocalPreviewAgentRun(run: WorkspaceTask): WorkspaceTask {
  const normalized = coerceTaskRecord(run) ?? {
    ...run,
    date: run.date || new Date().toISOString(),
    matterId: run.matterId || "",
    matterName: run.matterName || "Unlinked matter",
    executor: run.executor || "Agent",
    progress: typeof run.progress === "number" ? run.progress : 0,
    steps: Array.isArray(run.steps) ? run.steps : [],
    hoursSaved: typeof run.hoursSaved === "number" ? run.hoursSaved : 0,
    recoveredAED: typeof run.recoveredAED === "number" ? run.recoveredAED : 0,
    isReported: run.isReported === true,
  };

  if (!isBrowser()) return normalized;

  try {
    const existing = readLocalPreviewAgentRuns();
    const next = [normalized, ...existing.filter((candidate) => candidate.id !== normalized.id)].slice(0, 25);
    window.localStorage.setItem(LOCAL_PREVIEW_AGENT_RUNS_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(LOCAL_PREVIEW_AGENT_RUNS_EVENT));
  } catch {
    // Local preview should still proceed even if localStorage is unavailable.
  }

  return normalized;
}